import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, input } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { ModalStateService } from "src/app/services/modal-service/modal-state.service";

/**
 * Single cohesive config object replacing the previous 8 individual inputs.
 * Keeps the component's public API at one input while exposing the same
 * names internally via `computed()` accessors (template unchanged).
 */
export interface ChatModalConfig {
  type?: string;
  message?: string;
  waitingTime?: number;
  checkResponse?: () => string | null;
  onWaitingSuccess?: (message: string) => void;
  onWaitingTimeout?: () => void;
  onWaitingClose?: () => void;
  onLeaveMessage?: () => void;
}

@Component({
  selector: "app-chat-modal",
  templateUrl: "./chat-modal.component.html",
  styleUrls: ["./chat-modal.component.scss"],
  standalone: true,
  imports: [TranslateModule]
})
export class ChatModalComponent implements OnInit, OnDestroy {
  readonly config = input<ChatModalConfig>({});

  readonly type = computed(() => this.config().type ?? "failed");
  readonly message = computed(() => this.config().message ?? "");
  readonly waitingTime = computed(() => this.config().waitingTime ?? 0);
  readonly checkResponse = computed<() => string | null>(
    () => this.config().checkResponse ?? (() => null)
  );
  readonly onWaitingSuccess = computed<(message: string) => void>(
    () => this.config().onWaitingSuccess ?? (() => {})
  );
  readonly onWaitingTimeout = computed<() => void>(
    () => this.config().onWaitingTimeout ?? (() => {})
  );
  readonly onWaitingClose = computed<() => void>(
    () => this.config().onWaitingClose ?? (() => {})
  );
  readonly onLeaveMessage = computed<() => void>(
    () => this.config().onLeaveMessage ?? (() => {})
  );

  countdown: number = 0;
  timerInterval;
  modalState: ModalStateService;
  changeDetector: ChangeDetectorRef;

  constructor() {
    this.modalState = inject(ModalStateService);
    this.changeDetector = inject(ChangeDetectorRef);
  }

  ngOnInit(): void {
    this.startTimer();
  }

  ngOnDestroy(): void {
    clearInterval(this.timerInterval);
  }

  startTimer(): void {
    if (this.type() !== "waiting") return;

    this.countdown = this.waitingTime() || 15;
    this.changeDetector.detectChanges();
    this.timerInterval = setInterval(() => {
      const response = this.checkResponse()?.() ?? null;
      if (response) {
        this.closeModal();
        setTimeout(() => this.onWaitingSuccess()(response));
        return;
      }

      this.countdown -= 1;
      this.changeDetector.detectChanges();
      if (this.countdown <= 0) {
        this.closeModal();
        setTimeout(() => this.onWaitingTimeout()());
      }
    }, 1000);
  }

  closeModal(): void {
    clearInterval(this.timerInterval);
    if (this.type() === "waiting") {
      this.onWaitingClose()();
    }
    this.modalState.triggerDismiss();
  }

  leaveMessage(): void {
    this.closeModal();
    setTimeout(() => this.onLeaveMessage()());
  }
}